import React from 'react';
import { WorkflowApprovalStage } from '@/types/domain/nfa-lfa';
import { CheckCircle2, Clock, XCircle, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ApprovalStatusHeaderProps {
  stages: WorkflowApprovalStage[];
  className?: string;
}

export const ApprovalStatusHeader: React.FC<ApprovalStatusHeaderProps> = ({ stages, className }) => {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-4 shadow-2xs', className)}>
      <div className="flex items-center justify-between border-b border-border/60 pb-3 mb-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground-secondary flex items-center gap-2">
          Approval Workflow Stages ({stages.length})
        </h3>
        <span className="text-[10px] font-semibold text-foreground-muted">
          Sequential & Parallel Multi-Stage Pipeline
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2 custom-scrollbar">
        {stages.map((stage, idx) => {
          const isCompleted = stage.status === 'Approved';
          const isCurrent = stage.status === 'InProgress';
          const isRejected = stage.status === 'Rejected';
          const isDelegated = stage.status === 'Delegated';
          const isEscalated = stage.status === 'Escalated';

          return (
            <React.Fragment key={stage.id}>
              {idx > 0 && (
                <div
                  className={cn(
                    'h-0.5 w-8 flex-shrink-0 transition-colors',
                    isCompleted ? 'bg-primary' : 'bg-border'
                  )}
                />
              )}

              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all shadow-xs',
                    isCompleted && 'bg-primary text-primary-foreground',
                    isCurrent && 'bg-primary/20 text-primary border-2 border-primary animate-pulse',
                    isRejected && 'bg-rose-500 text-white',
                    isDelegated && 'bg-amber-500 text-white',
                    isEscalated && 'bg-purple-600 text-white',
                    !isCompleted && !isCurrent && !isRejected && !isDelegated && !isEscalated && 'bg-muted text-foreground-muted border border-border'
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : isRejected ? (
                    <XCircle className="h-4 w-4" />
                  ) : isDelegated ? (
                    <ArrowRightLeft className="h-4 w-4" />
                  ) : isEscalated ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : isCurrent ? (
                    <Clock className="h-4 w-4" />
                  ) : (
                    <span>{stage.stageNumber}</span>
                  )}
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-foreground truncate max-w-[130px]">
                      {stage.stageName}
                    </span>
                    {stage.approvalMode === 'PARALLEL' && (
                      <span className="rounded bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[9px] font-bold px-1">
                        PARALLEL
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-foreground-muted">
                    {stage.approverName} ({stage.approverRole})
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
